import styled, { css } from 'styled-components/macro';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

// One button for the whole app. Pick a look with $variant and a size with $size
// rather than restyling: colours, radius, height and font all come from here.
//
//   <Button $variant="create" $size="sm">Add</Button>
//   <Button $fullWidth>Sign in</Button>            full width everywhere
//   <Button $fullWidthOnMobile>Save</Button>       full width below the md breakpoint
//
// Buttons size to their content (with a minimum width per size) and never
// stretch to fill a flex column unless asked to.

const onBrand = ({ theme }) =>
  theme.isClassRoomDark ? theme.textLight : theme.headerColour;

const variants = {
  primary: css`
    background: ${({ theme }) => theme.buttonPrimary};
    color: ${onBrand};
  `,
  brand: css`
    background: ${({ theme }) => theme.classroomHighlight};
    color: ${onBrand};
  `,
  lesson: css`
    background: ${({ theme }) => theme.lessonHighlight};
    color: ${({ theme }) =>
      theme.isLessonDark ? theme.textLight : theme.headerColour};
  `,
  create: css`
    background: ${({ theme }) => theme.buttonCreate};
    color: ${onBrand};
  `,
  update: css`
    background: ${({ theme }) => theme.buttonUpdate};
    color: ${onBrand};
  `,
  danger: css`
    background: ${({ theme }) => theme.buttonWarning};
    color: ${({ theme }) => theme.textLight};
  `,
  secondary: css`
    background: ${neutral.surface};
    color: ${({ theme }) => theme.headerColour};
    box-shadow: inset 0 0 0 1px ${neutral.borderStrong}, ${shadow.sm};

    &:hover {
      filter: none;
      background: ${neutral.subtle};
    }
  `,
  dangerSecondary: css`
    background: ${neutral.surface};
    color: ${({ theme }) => theme.buttonWarning};
    box-shadow: inset 0 0 0 1px ${neutral.borderStrong}, ${shadow.sm};

    &:hover {
      filter: none;
      background: ${({ theme }) => theme.buttonWarning};
      color: ${({ theme }) => theme.textLight};
    }
  `,
};

const sizes = {
  sm: css`
    min-height: 3.6rem;
    min-width: 8rem;
    padding: 0 ${space[3]};
    font-size: ${fontSize.sm};

    @media (pointer: coarse) {
      min-height: ${layout.touchTarget};
    }
  `,
  md: css`
    min-height: ${layout.touchTarget};
    min-width: 12rem;
    padding: 0 ${space[5]};
    font-size: ${fontSize.md};
  `,
  lg: css`
    min-height: 5.2rem;
    min-width: 16rem;
    padding: 0 ${space[6]};
    font-size: ${fontSize.lg};
  `,
};

export const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  width: fit-content;
  max-width: 100%;
  height: auto;
  border: 0;
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  font-family: inherit;
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.tight};
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: filter ${transition.fast}, box-shadow ${transition.fast},
    background ${transition.fast}, transform ${transition.fast};

  &:hover {
    filter: brightness(0.95);
    box-shadow: ${shadow.md};
  }
  &:active {
    transform: translateY(1px);
  }

  ${({ $size = 'md' }) => sizes[$size] || sizes.md}
  ${({ $variant = 'primary' }) => variants[$variant] || variants.primary}
  &:disabled,
  &[aria-disabled='true'] {
    background: ${({ theme }) => theme.buttonDisabled};
    color: ${({ theme }) => theme.textLight};
    box-shadow: none;
    filter: none;
    transform: none;
    cursor: default;
  }
  ${focusRing}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
  ${({ $fullWidthOnMobile }) =>
    $fullWidthOnMobile &&
    css`
      width: 100%;
      ${media.md`
        width: fit-content;
      `}
    `}
`;

export const Button = styled.button`
  ${buttonStyles}
`;

const withVariant = (variant) =>
  styled(Button).attrs(({ $variant }) => ({ $variant: $variant || variant }))``;

export const CreateButton = withVariant('create');
export const DeleteButton = withVariant('danger');
export const SaveChangesButton = styled(withVariant('update'))`
  align-self: center;
`;

// Round, icon-only buttons (edit, delete, close, copy...). Always 44px so they're easy to tap.
// $tone: 'default' | 'danger' (turns red on hover) | 'onDark' (on coloured headers)
export const iconButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  margin: 0;
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  background: none;
  color: ${neutral.textMuted};
  cursor: pointer;
  transition: background ${transition.fast}, color ${transition.fast};

  &:hover {
    background: ${neutral.subtle};
    color: ${({ theme }) => theme.headerColour};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  ${focusRing}

  ${({ $tone }) =>
    $tone === 'danger' &&
    css`
      &:hover {
        color: ${({ theme }) => theme.buttonDelete};
      }
    `}
  ${({ $tone }) =>
    $tone === 'onDark' &&
    css`
      color: ${({ theme }) => theme.textLight};
      &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: ${({ theme }) => theme.textLight};
      }
    `}
`;

export const IconButton = styled.button`
  ${iconButtonStyles}
`;
